import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Box, Button, Card, Flex, Grid, Heading, Text } from '@radix-ui/themes'
import { PlusIcon, UploadIcon } from '@radix-ui/react-icons'
import { useDropzone } from 'react-dropzone'
import cn from 'classnames'
import { useAuth } from '~/auth/AuthProvider'
import { ImageLightbox, openOnPlainClick } from '~/components/ImageLightbox'
import { IMAGE_DROP_ACCEPT, IMAGE_MAX_BYTES } from '~/features/installations/constants'
import { toUploadableImage } from '~/features/installations/heic'
import {
  deleteInstallationPhoto,
  listInstallationPhotos,
  uploadInstallationPhoto
} from '~/features/installations/service'
import { PhotoLightboxFooter } from './PhotoLightboxFooter'

const MAX_PHOTOS = 20

const photosQueryKey = (installationId: number) => ['installation-photos', installationId] as const

interface PhotoThumbnailProps {
  url: string | null
  alt: string
  onOpen: () => void
}

export const PhotoThumbnail = ({ url, alt, onOpen }: PhotoThumbnailProps) => {
  const [errored, setErrored] = useState(false)

  return (
    <Box className="aspect-square overflow-hidden rounded-[var(--radius-2)] border border-[color:var(--gray-5)] bg-[var(--gray-2)]">
      {errored || !url ? (
        <Flex align="center" justify="center" className="h-full w-full">
          <Text size="1" color="gray">
            Failed to load
          </Text>
        </Flex>
      ) : (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="block h-full w-full"
          onClick={openOnPlainClick(onOpen)}
        >
          <img src={url} alt={alt} className="h-full w-full object-cover" onError={() => setErrored(true)} />
        </a>
      )}
    </Box>
  )
}

interface InstallationPhotosProps {
  installationId: number
  canEdit?: boolean
}

export const InstallationPhotos = ({ installationId, canEdit = true }: InstallationPhotosProps) => {
  const { supabaseClient } = useAuth()
  const queryClient = useQueryClient()
  const [actionError, setActionError] = useState<string | null>(null)
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  const queryKey = photosQueryKey(installationId)

  const {
    data: photos = [],
    isLoading,
    error: listError
  } = useQuery({
    queryKey,
    queryFn: () => listInstallationPhotos(supabaseClient, installationId)
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey })

  const { mutate: uploadPhotos, isPending: isUploading } = useMutation({
    mutationFn: (files: File[]) =>
      Promise.all(
        files.map(async (file) =>
          uploadInstallationPhoto(supabaseClient, installationId, await toUploadableImage(file))
        )
      ),
    onSuccess: () => setActionError(null),
    onError: (error) => setActionError(error instanceof Error ? error.message : 'Upload failed'),
    // Settled, not success: a partially failed batch still stored some photos — show them.
    onSettled: invalidate
  })

  const { mutate: removePhoto } = useMutation({
    mutationFn: (fileName: string) => deleteInstallationPhoto(supabaseClient, installationId, fileName),
    onSuccess: () => {
      setActionError(null)
      invalidate()
    },
    onError: (error) => setActionError(error instanceof Error ? error.message : 'Delete failed')
  })

  const atLimit = photos.length >= MAX_PHOTOS
  const canDrop = canEdit && !isUploading && !atLimit
  const openPhoto = openIndex === null ? null : photos[openIndex]

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    multiple: true,
    accept: IMAGE_DROP_ACCEPT,
    maxSize: IMAGE_MAX_BYTES,
    disabled: !canDrop,
    noClick: photos.length > 0,
    onDropRejected: (rejections) => setActionError(rejections[0]?.errors[0]?.message ?? 'File not accepted'),
    onDrop: (accepted) => {
      if (accepted.length === 0) return
      if (photos.length + accepted.length > MAX_PHOTOS) {
        setActionError(`You can upload up to ${MAX_PHOTOS} photos.`)
        return
      }
      setActionError(null)
      uploadPhotos(accepted)
    }
  })

  return (
    <Card>
      <Flex direction="column" gap="3" className="h-full">
        <Flex align="center" justify="between" gap="2">
          <Heading size="3">Installation photos</Heading>
          <Flex align="center" gap="3">
            <Text size="1" color={atLimit ? 'amber' : 'gray'}>
              {photos.length}/{MAX_PHOTOS}
            </Text>
            {/* Header, not the end of the list: at one tile per row the list's own end is
                several screens down. */}
            {canDrop && photos.length > 0 ? (
              <Button type="button" size="1" variant="ghost" onClick={open}>
                <PlusIcon />
                Add
              </Button>
            ) : null}
          </Flex>
        </Flex>

        <Box
          {...(canDrop ? getRootProps() : {})}
          position="relative"
          className={cn(
            'flex min-h-[12rem] flex-1 flex-col rounded-[var(--radius-3)] border p-3',
            canDrop && 'cursor-pointer border-dashed',
            isDragActive ? 'border-[color:var(--accent-9)] bg-[var(--accent-2)]' : 'border-[color:var(--gray-5)]'
          )}
        >
          {canDrop && <input {...getInputProps()} />}

          {isLoading ? (
            <Flex align="center" justify="center" className="flex-1">
              <Text size="2" color="gray">
                Loading…
              </Text>
            </Flex>
          ) : listError ? (
            <Flex align="center" justify="center" className="flex-1">
              <Text size="2" color="red">
                Failed to load photos. Refresh the page to retry.
              </Text>
            </Flex>
          ) : photos.length === 0 ? (
            <Flex direction="column" align="center" justify="center" gap="2" className="flex-1">
              <UploadIcon width="24" height="24" color="var(--gray-9)" />
              <Text size="2" color="gray" align="center" className="px-3">
                {canEdit ? (isDragActive ? 'Drop images here' : 'Drag images or click to upload') : 'No photos'}
              </Text>
              {canEdit && (
                <Text size="1" color="gray">
                  PNG / JPG / WebP / GIF · up to 10MB each
                </Text>
              )}
            </Flex>
          ) : (
            // Out of flow, and the grid inside it left in flow: a scroll container reports its
            // full content height to the grid row above, growing the card per thumbnail, while a
            // grid with a definite height compresses its auto rows and overlaps the tiles.
            <Box
              position="absolute"
              inset="3"
              className="overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              <Grid columns="3" gap="2">
                {photos.map((photo, index) => (
                  <PhotoThumbnail
                    key={photo.name}
                    url={photo.url}
                    alt="Installation"
                    onOpen={() => setOpenIndex(index)}
                  />
                ))}
              </Grid>
            </Box>
          )}
        </Box>

        {isUploading && (
          <Text size="1" color="gray">
            Uploading…
          </Text>
        )}
        {actionError && (
          <Text size="1" color="red">
            {actionError}
          </Text>
        )}
      </Flex>

      <ImageLightbox
        images={photos.map((photo) => photo.url)}
        openIndex={openIndex}
        onOpenChange={setOpenIndex}
        footer={
          openPhoto ? (
            <PhotoLightboxFooter
              sharePath={openPhoto.path}
              onDelete={
                canEdit
                  ? {
                      run: () => removePhoto(openPhoto.name),
                      confirmTitle: 'Delete photo?',
                      confirmDescription: 'This permanently removes the photo. This cannot be undone.'
                    }
                  : undefined
              }
            />
          ) : null
        }
      />
    </Card>
  )
}
